import { useState } from "react";
import { BookingStatus } from "@repo/database";
import { groupBy } from "@repo/utils";
import { format, isSameDay } from "date-fns";
import { useWatch } from "react-hook-form";
import { z } from "zod";

import { api } from "~/lib/api";
import { useTempBookingStore } from "~/stores/use-booking-store";
import { Button } from "../ui/button";
import { Form, FormControl, FormField, FormItem, useForm } from "../ui/form";
import { Input } from "../ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import BookingItem from "./booking-item";

export default function BookingSearch({ onClose }: { onClose: () => void }) {
  const { selectedDate } = useTempBookingStore();
  const [selectedTab, setSelectedTab] = useState<"this_day" | "all_days">(
    "this_day",
  );

  const form = useForm({
    schema: z.object({
      search: z.string().min(3),
    }),
    defaultValues: {
      search: "",
    },
  });

  const searchValue = useWatch({ control: form.control, name: "search" });

  const { data } = api.booking.search.useQuery(searchValue, {
    enabled: searchValue.length > 2,
    initialData: [],
  });

  const groupByDate = groupBy(data, "date");

  const sameDayBookings = data.filter((booking) =>
    isSameDay(booking.startTime, selectedDate),
  );

  return (
    <Form {...form}>
      <form className="flex flex-col">
        <div className="border-b p-4 sticky top-0 z-[999] bg-popover">
          <FormField
            control={form.control}
            name="search"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    onClear={() => field.onChange("")}
                    autoComplete="off"
                    {...field}
                    placeholder="Search"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <Tabs defaultValue="this_day" className="w-full p-0">
          <TabsList className="w-full bg-popover rounded-none p-0 items-stretch">
            <TabsTrigger
              onClick={() => setSelectedTab("this_day")}
              className="flex-grow rounded-none"
              value="this_day"
            >
              This day
            </TabsTrigger>
            <TabsTrigger
              onClick={() => setSelectedTab("all_days")}
              className="flex-grow rounded-none"
              value="all_days"
            >
              All days
            </TabsTrigger>
          </TabsList>
          <TabsContent value="this_day" className="mt-0">
            <div className="overflow-auto small-scrollbar">
              {sameDayBookings
                .sort((a, b) => b.startTime.getTime() - a.startTime.getTime())
                .map((booking) => (
                  <BookingItem
                    onClick={() => {
                      onClose();
                    }}
                    booking={booking}
                    key={booking.id}
                    showProvider
                    showTime
                    showStatus={booking.bookingStatus !== BookingStatus.pending}
                  />
                ))}
            </div>
          </TabsContent>
          <TabsContent value="all_days" className="mt-0">
            <div className="overflow-auto small-scrollbar">
              {Object.entries(groupByDate)
                .sort((a, b) => {
                  const firstBooking = a[1][0];
                  if (!firstBooking) return 1;
                  const secondBooking = b[1][0];
                  if (!secondBooking) return -1;
                  return (
                    secondBooking.startTime.getTime() -
                    firstBooking.startTime.getTime()
                  );
                })
                .map(([date, bookings]) => {
                  const firstBooking = bookings[0];
                  if (!firstBooking) return null;
                  const formattedDate = format(
                    firstBooking.startTime,
                    "EEEE d MMMM yyyy",
                  );
                  return (
                    <div key={date} className="flex flex-col">
                      <div className="bg-background-tertiary px-2">
                        <span className="text-lg font-medium">
                          {formattedDate}
                        </span>
                      </div>

                      {bookings
                        .sort(
                          (a, b) =>
                            b.startTime.getTime() - a.startTime.getTime(),
                        )
                        .map((booking) => (
                          <BookingItem
                            onClick={() => {
                              onClose();
                            }}
                            booking={booking}
                            key={booking.id}
                            showProvider
                            showTime
                            showStatus={
                              booking.bookingStatus !== BookingStatus.pending
                            }
                          />
                        ))}
                    </div>
                  );
                })}
            </div>
          </TabsContent>
        </Tabs>

        {searchValue.length > 2 &&
          sameDayBookings.length === 0 &&
          selectedTab === "this_day" && (
            <div className="flex flex-col gap-2 items-center p-4 border-t">
              <p>No results found for this day</p>
              <Button>Search all days</Button>
            </div>
          )}

        {searchValue.length < 3 && (
          <div className="p-4 border-t">
            <p className="text-center">Enter at least 3 characters to search</p>
          </div>
        )}

        {searchValue.length > 2 &&
          data.length === 0 &&
          selectedTab !== "this_day" && (
            <div className="p-4 border-t">
              <p className="text-center">No results found</p>
            </div>
          )}
      </form>
    </Form>
  );
}
